import { useState, useEffect, useCallback } from "react";
import { API_CONFIG } from "../config/apiConfig";
import { apiClient } from "../utils/apiClient";

interface PalletResponse {
  success: boolean;
  data?: Array<{ pallet: string; subinventoryCode: string }>;
  message?: string;
}

interface UsePalletDetailsProps {
  data: Array<{ pallet: string; subinventoryCode: string }>;
  isLoading: boolean;
  error: string | null;
  refetch: (
    tosite: string,
    palletval?: string,
    callback?: (data: Array<{ pallet: string; subinventoryCode: string }>) => void,
    ordertype?: string
  ) => Promise<void>;
}

type CallbackFunction = (data: Array<{ pallet: string; subinventoryCode: string }>) => void;

export const usePalletDetails = (
  tosite?: string,
  palletval?: string,
  executeApiOnload = true,
  ordertype?: string
): UsePalletDetailsProps => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Array<{ pallet: string; subinventoryCode: string }>>([]);

  const handleEmptyResponse = useCallback((callback?: CallbackFunction) => {
    const emptyData: Array<{ pallet: string; subinventoryCode: string }> = [];
    setData(emptyData);
    callback?.(emptyData);
  }, []);

  const fetchData = useCallback(
    async (tosite: string, palletval?: string, callback?: CallbackFunction, ordertype?: string): Promise<void> => {
      if (!tosite) {
        handleEmptyResponse(callback);
        return;
      }

      setIsLoading(true);
      setError(null);
      // Don't clear existing data during refetch to avoid dropdown flickering

      try {
        const { data: responseData } = await apiClient.get<PalletResponse>(
          API_CONFIG.ENDPOINTS.GET_PALLET_DETAILS,
          {
            params: {
              tosite: String(tosite || '').trim(),
              palletval: palletval ? String(palletval).trim() : '',
              ordertype: ordertype ? String(ordertype).trim() : '',
            },
          }
        );

        if (responseData.success && Array.isArray(responseData.data)) {
          setData(responseData.data);
          callback?.(responseData.data);
        } else {
          handleEmptyResponse(callback);
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message;
        setError(errorMessage);
        console.error("Pallet Details Error:", errorMessage);
        handleEmptyResponse(callback);
      } finally {
        setIsLoading(false);
      }
    },
    [handleEmptyResponse]
  );

  // Memoized refetch function
  const refetch = useCallback(
    async (tosite: string, palletval?: string, callback?: CallbackFunction, ordertype?: string): Promise<void> => {
      await fetchData(tosite, palletval, callback, ordertype);
    },
    [fetchData]
  );

  // Initial data fetch
  useEffect(() => {
    if (executeApiOnload && tosite) {
      fetchData(tosite, palletval, undefined, ordertype);
    }
  }, [executeApiOnload, tosite, palletval, fetchData]);

  return { data, isLoading, error, refetch };
};